<template>
  <SettingsPageSection :title="$t('Worker compliance review')">
    <template #subtitle>
      <WdsText secondary>
        {{
          $t(
            "When enabled, your team must review and clear each worker's compliance before that worker can be engaged.",
          )
        }}
      </WdsText>
    </template>

    <WdsToggle
      v-model:checked="enabled"
      :label="$t('Require my team to review worker compliance')"
      :disabled="isSaving"
      data-test="compliance-tracker-toggle"
      @update:checked="save"
    />

    <WdsAlert v-if="errorMessage" error class="tw-mt-3">
      {{ errorMessage }}
    </WdsAlert>
    <WdsAlert v-else-if="showSaved" success closable class="tw-mt-3" @close="showSaved = false">
      {{ $t('Settings saved') }}
    </WdsAlert>
  </SettingsPageSection>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { WdsAlert, WdsText, WdsToggle } from '@worksome/wds-components'
import gql from 'graphql-tag'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import SettingsPageSection from '@/components/compliances/settings/SettingsPageSection.vue'

const settingsQuery = gql`
  query complianceTrackerSetting {
    me {
      activeAccount {
        __typename
        ... on Company {
          globalId
          settings {
            compliance_tracker_enabled
          }
        }
      }
    }
  }
`

const updateCompanyMutation = gql`
  mutation updateCompanyComplianceTracker($globalId: ID!, $enabled: Boolean!) {
    updateCompany(input: { globalId: $globalId, settings: { compliance_tracker_enabled: $enabled } }) {
      globalId
      settings {
        compliance_tracker_enabled
      }
    }
  }
`

const { result } = useQuery(settingsQuery)

const company = computed(() => {
  const account = result.value?.me?.activeAccount
  return account?.__typename === 'Company' ? account : null
})

const enabled = ref(company.value?.settings?.compliance_tracker_enabled ?? false)

watch(
  company,
  (c) => {
    if (c) enabled.value = c.settings?.compliance_tracker_enabled ?? false
  },
  { once: true },
)

const {
  mutate: updateCompany,
  loading: isSaving,
  error: mutationError,
} = useMutation(updateCompanyMutation)

const errorMessage = computed(() => mutationError.value?.graphQLErrors?.[0]?.message ?? '')
const showSaved = ref(false)

async function save(value: boolean) {
  if (!company.value) return

  showSaved.value = false
  const saveResult = await updateCompany({
    globalId: company.value.globalId,
    enabled: value,
  })

  if (saveResult) showSaved.value = true
}
</script>
